import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { http, getJson } from "@/lib/http";
import { useToast } from "@/hooks/use-toast";

interface FormatOnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retailerId: number;
  mediaFormatId: number;
  retailerName: string;
  mediaFormatName: string;
  visualClassId: number | null;
  onComplete: () => void;
}

export function FormatOnboardingWizard({
  open,
  onOpenChange,
  retailerId,
  mediaFormatId,
  retailerName,
  mediaFormatName,
  visualClassId,
  onComplete,
}: FormatOnboardingWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const { toast } = useToast();

  // Prompt State
  const [promptText, setPromptText] = useState("");
  const [promptId, setPromptId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setStep(1);
    }
  }, [open]);

  // Fetch prompt if visual class exists
  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<{ id: number; visualClassId: number; promptText: string }[]>({
    queryKey: ['/api/admin/ai-triage/prompts'],
    queryFn: async () => {
      const res = await http('/api/admin/ai-triage/prompts');
      if (!res.ok) throw new Error("Failed to fetch prompts");
      return res.json();
    },
    enabled: open && !!visualClassId && step === 2,
  });

  useEffect(() => {
    if (prompts && visualClassId) {
      const existing = prompts.find(p => p.visualClassId === visualClassId);
      if (existing) {
        setPromptText(existing.promptText);
        setPromptId(existing.id);
      }
    }
  }, [prompts, visualClassId]);

  const savePromptMutation = useMutation({
    mutationFn: async () => {
      const method = promptId ? 'PATCH' : 'POST';
      const url = promptId ? `/api/admin/ai-triage/prompts/${promptId}` : '/api/admin/ai-triage/prompts';
      const body = promptId 
        ? { promptText } 
        : { visualClassId, promptText, isActive: true };

      const res = await http(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to save prompt");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Prompt saved" });
      setStep(3);
    },
    onError: (err: any) => {
      toast({ title: "Failed to save prompt", description: err.message, variant: "destructive" });
    }
  });

  // Test Run State
  const { data: testImage, refetch: fetchTestImage, isLoading: isLoadingTestImage } = useQuery<any>({
    queryKey: ['/api/admin/ai-triage/test-image', retailerId, mediaFormatId],
    queryFn: async () => {
      // Find a recent submission for this retailer + format combo
      const res = await http(`/api/review/submissions?retailerId=${retailerId}&mediaFormatId=${mediaFormatId}&limit=1&status=PENDING`);
      const data = await res.json();
      return data.jobs?.[0] || null;
    },
    enabled: false, // Only fetch when entering step 3
  });

  const runTestMutation = useMutation({
    mutationFn: async (submissionId: number) => {
      const response = await http("/api/admin/ai-triage/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submissionId }),
      });
      if (!response.ok) throw new Error("Failed to run test");
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Test enqueued. It will appear in tracker." });
      // In a more robust version, we'd poll for result here.
    }
  });

  // Enable AI
  const enableAiMutation = useMutation({
    mutationFn: async () => {
      const res = await http(`/api/admin/ai-triage/mappings/${retailerId}/${mediaFormatId}/enable`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error("Failed to enable AI");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Photo checks enabled", description: "New submissions for this format will get photo-check suggestions." });
      onComplete();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Failed to enable", description: err.message, variant: "destructive" });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Set up spotting for {retailerName} — {mediaFormatName}</DialogTitle>
          <DialogDescription>
            {step === 1 && "Step 1: How format recognition improves"}
            {step === 2 && "Step 2: Define photo-check rules"}
            {step === 3 && "Step 3: Test the check"}
            {step === 4 && "Step 4: Activation"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You are about to enable photo checks for this format using plain-English standards.
                No special training step is required to start.
              </p>
              <div className="bg-primary/5 p-4 rounded border border-primary/20 space-y-2">
                <h4 className="font-medium text-sm text-primary">Getting better over time</h4>
                <p className="text-xs text-muted-foreground">
                  When reviewers confirm decisions, those photos can be used later to improve how reliably
                  this format is spotted. Everyday work does not require opening specialist tools.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Define what the photo check should look for. Provide plain English instructions for this visual class.
              </p>
              {!visualClassId ? (
                <div className="text-sm text-red-500 bg-red-50 p-3 rounded">
                  Please link this format to a recognition category in the table before writing standards.
                </div>
              ) : (
                <Textarea 
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder="e.g. Ensure the product is fully visible and not damaged..."
                  className="min-h-[150px]"
                />
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Let's pull a recent submission for this format to test your standards.
              </p>
              
              {!testImage ? (
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={() => fetchTestImage()} disabled={isLoadingTestImage}>
                    {isLoadingTestImage ? "Finding image..." : "Find Recent Submission"}
                  </Button>
                </div>
              ) : (
                <Card>
                  <CardContent className="pt-6 space-y-4 flex flex-col items-center">
                    <img src={testImage.evidenceUrl} alt="Test evidence" className="max-h-48 rounded shadow-sm" />
                    <Button onClick={() => runTestMutation.mutate(testImage.id)} disabled={runTestMutation.isPending}>
                      {runTestMutation.isPending ? "Running..." : "Run AI Test"}
                    </Button>
                    {runTestMutation.isSuccess && (
                      <p className="text-xs text-green-600">Test enqueued! Proceed to enable AI.</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You're ready to go live.
              </p>
              <ul className="text-sm space-y-2 list-disc pl-5 text-muted-foreground">
                <li>All new submissions for this format will be evaluated by photo checks.</li>
                <li>Reviewers will see the AI suggestion in the Tracker.</li>
                <li>Verified decisions help build the dedicated format-detector dataset.</li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center w-full sm:justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1 as any)}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            
            {step === 1 && (
              <Button onClick={() => setStep(2)}>Next: Define Rules</Button>
            )}
            
            {step === 2 && (
              <Button 
                onClick={() => savePromptMutation.mutate()} 
                disabled={!visualClassId || savePromptMutation.isPending}
              >
                {savePromptMutation.isPending ? "Saving..." : "Save & continue"}
              </Button>
            )}

            {step === 3 && (
              <Button onClick={() => setStep(4)}>Skip Test & Next</Button>
            )}

            {step === 4 && (
              <Button 
                onClick={() => enableAiMutation.mutate()}
                disabled={enableAiMutation.isPending}
              >
                {enableAiMutation.isPending ? "Enabling..." : "Enable photo checks"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
